// Session drill-down modal.
//
// Opened when a user clicks a row in BotEffectiveness / BotEffectivenessByDay /
// BookingLeadTime / RoutingFlow. Shows the raw sessions behind that aggregate
// with a link to Call Detail per row — keeps the user in Analytics instead
// of making them guess an ANI filter in Call Search.

import React, { useMemo, useState } from 'react';
import { ActionIcon, Badge, Group, Modal, ScrollArea, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { IconExternalLink, IconSearch } from '@tabler/icons-react';
import { fmtDuration, fmtTs } from '../utils';

/**
 * @param {boolean} opened
 * @param {() => void} onClose
 * @param {string} title     — e.g. "OrthoMain — 2026-10-20"
 * @param {string} subtitle  — e.g. "47 sessions · breakdown by outcome"
 * @param {Array} sessions   — contactAIAgentSession rows for this bucket
 * @param {(interactionId, window, cachedAiSessions?) => void} onViewSession
 *   — called when a session row's "open" button is clicked. Analytics builds
 *     this from WxccAiOps's setView so the modal doesn't need routing context.
 * @param {{from: number, to: number} | null} window
 *   — pass-through to setView so Call Detail inherits the Analytics window
 */
export function SessionListModal({ opened, onClose, title, subtitle, sessions, onViewSession, window }) {
  const [filter, setFilter] = useState('');

  const rows = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const list = (sessions ?? []).map((s) => ({
      key: s.sessionId ?? s.interactionId ?? Math.random(),
      sessionId: s.sessionId ?? '',
      interactionId: s.interactionId ?? '',
      agentName: s.agentName ?? '',
      ani: s.ani ?? '',
      outcome: s.interactionOutcome ?? '',
      duration: s.duration ?? null,
      messages: s.noOfTotalMessages ?? null,
      start: s.sessionStartTime ?? null,
    }));
    if (!term) return list;
    return list.filter((r) =>
      [r.sessionId, r.interactionId, r.ani, r.agentName, r.outcome]
        .some((v) => String(v ?? '').toLowerCase().includes(term)),
    );
  }, [sessions, filter]);

  return (
    <Modal opened={opened} onClose={onClose} size="xl" title={<ModalTitle title={title} subtitle={subtitle} count={rows.length} total={sessions?.length ?? 0} />}>
      <TextInput
        size="xs"
        placeholder="Filter by session id, interaction id, ANI, or outcome…"
        leftSection={<IconSearch size={14} />}
        value={filter}
        onChange={(e) => setFilter(e?.currentTarget?.value ?? e?.target?.value ?? '')}
        mb="xs"
      />
      <ScrollArea.Autosize mah={520}>
        <Table verticalSpacing="xs" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Start</Table.Th>
              <Table.Th>Bot</Table.Th>
              <Table.Th>ANI</Table.Th>
              <Table.Th>Outcome</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Duration</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Msgs</Table.Th>
              <Table.Th>Session ID</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text size="xs" c="dimmed">No sessions match the filter.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((r) => (
              <Table.Tr key={r.key}>
                <Table.Td><Text size="xs">{r.start ? fmtTs(r.start) : '—'}</Text></Table.Td>
                <Table.Td><Text size="xs">{r.agentName || '—'}</Text></Table.Td>
                <Table.Td><Text size="xs" style={{ fontFamily: 'monospace' }}>{r.ani || '—'}</Text></Table.Td>
                <Table.Td><OutcomeBadge outcome={r.outcome} /></Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="xs">{r.duration != null ? fmtDuration(r.duration) : '—'}</Text>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="xs">{r.messages ?? '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>{shortId(r.sessionId)}</Text>
                </Table.Td>
                <Table.Td>
                  {onViewSession && r.interactionId && (
                    <Tooltip label="Open in Call Detail">
                      <ActionIcon
                        size="sm"
                        variant="light"
                        onClick={() => {
                          onViewSession(r.interactionId, window);
                          onClose();
                        }}
                      >
                        <IconExternalLink size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
    </Modal>
  );
}

function ModalTitle({ title, subtitle, count, total }) {
  return (
    <Group gap="xs" align="baseline">
      <Text fw={600}>{title}</Text>
      <Text size="xs" c="dimmed">
        {subtitle}
      </Text>
      {count !== total && <Badge size="xs" color="gray" variant="light">{count} of {total}</Badge>}
    </Group>
  );
}

function OutcomeBadge({ outcome }) {
  if (!outcome) return <Text size="xs" c="dimmed">—</Text>;
  const color =
    outcome === 'HANDLED' || outcome === 'COMPLETED' ? 'teal' :
    outcome === 'ESCALATED' ? 'blue' :
    outcome === 'ABANDONED' ? 'orange' :
    outcome === 'FAILED' ? 'red' : 'gray';
  return <Badge size="xs" color={color} variant="light">{outcome}</Badge>;
}

function shortId(s) {
  if (!s) return '';
  return s.length > 16 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s;
}
